import { Button, Result, ResultProps, Space } from 'antd'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { MouseEvent, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useIntercom } from 'react-use-intercom'

import { CalendlyModal } from '../CalendlyModal'
import { ampli } from '@/ampli'
import { PaywallModalComponentId } from '@/components/common/modals/PaywallModal/PaywallModal'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { PaywallState } from '@/hooks/usePaywallModal'
import { buildI18nFunction } from '@/utils/messages'

type PaywallResultProps = {
  feature: PaywallModalComponentId
  onClick: (e: any) => void
  paywall: PaywallState
  callbacks: {
    closeModal: (e: any) => void
  }
} & ResultProps

const COMPONENT_ID = 'PaywallModal.ResultPaywall'

export function PaywallResult({
  callbacks,
  feature,
  paywall,
  ...props
}: PaywallResultProps): JSX.Element {
  const { workspace } = useWorkspaceContext()
  const { showNewMessage: showIntercom } = useIntercom()
  const t = buildI18nFunction(useIntl().formatMessage)

  const router = useRouter()

  useEffect(() => {
    ampli.isLoaded &&
      ampli.paywallViewed({ feature, pathname: router.pathname })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature])

  const handleCallToActionClick = (
    e: MouseEvent<HTMLElement, globalThis.MouseEvent>,
    paywall: PaywallState
  ) => {
    if (typeof paywall?.callToActionHref?.length !== 'undefined')
      router.push(paywall.callToActionHref)
    else showIntercom()

    props?.onClick?.(e)
  }

  const buttons = [
    <Button
      type="primary"
      onClick={(e) => handleCallToActionClick(e, paywall)}
      key={'Paywall.callToAction'}
    >
      {t(`${COMPONENT_ID}.${feature}.callToAction`)}
    </Button>,
  ]

  if (
    workspace?.descriptor?.trial === 'AVAILABLE' &&
    workspace?.on_boarding_status !== 'PENDING' &&
    workspace?.status === 'FREEMIUM'
  ) {
    buttons.splice(
      0,
      1,
      <Space key={'Paywall.callToAction'}>
        <CalendlyModal>
          {({ toggleCalendlyModal }) => (
            <Button
              key={'Paywall.startFreeTrial'}
              onClick={(e) => {
                toggleCalendlyModal(true)
                ampli.tryFullVersionClicked({
                  feature,
                  pathname: router.pathname,
                })
                callbacks.closeModal(e)
              }}
              type="primary"
            >
              {t(`${COMPONENT_ID}.freemium.callToAction`)}
            </Button>
          )}
        </CalendlyModal>
      </Space>
    )
  }

  return (
    <Result
      icon={
        <Image
          alt="Upgrade to unlock this feature"
          height={280}
          objectFit="contain"
          priority
          src={'/images/illustrations/paywall.svg'}
          width={280}
        />
      }
      title={t(`${COMPONENT_ID}.${feature}.title`)}
      subTitle={t(`${COMPONENT_ID}.${feature}.subtitle`)}
      extra={buttons}
      {...props}
    />
  )
}
